import React, { useEffect, useState } from "react";
import Table, { Row, Data } from "../../components/ui/table/table";

import CardService, { fetchCardLoadReport } from "../../api/card-service";
import WalletService, { fetchReportPrefund } from "../../api/wallet-service";

import { useQuery } from "@tanstack/react-query";

import {
  getFormattedDate,
  getAmountWithCurrency,
  formatAmount,
} from "../../utils/utils";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageWrapper from "../../layouts/PageWrapper";
import Section from "../../layouts/Section";

import { Input, Select } from "../../components/form/formControls";
import { StatusBadge } from "../../components/StatusBadge";

import SubUserSelector from "./SubUserSelector";

import Pagination from "../../components/Pagination";
import { useSelector } from "react-redux";
import { useDebounce } from "../../hooks/useDebounce";
import { CARDS_API_URL } from "../../api/urlMetaData";
import { Button } from "../../components/ui/buttons/Button";
import { downloadReporPrefund } from "../../utils/downloadReport";
import ViewDocumentDrawer from "./ViewDocumentDrawer";
import { FileText } from "react-feather";
import { usePagination } from "../../hooks/usePagination";
import { APP_USERS } from "../../consts/AppRoles";
import { useLocale } from "../../hooks/useLocale";

const downloadReport = async ({ userHashId, dateRange }) => {
  try {
    const url = `${CARDS_API_URL.DOWNLOAD_CARD_LOAD_REPORT_CLIENT}/${userHashId}?daterange=${dateRange}`;
    const res = await CardService.get(url);
    return res?.data();
  } catch (error) {}
};

const ReportsCardLoad = () => {
  // globals
  const inputDate = useSelector((s) => s.global.inputDate);
  const currentUser = useSelector((s) => s.auth.currentUser);
  const { page } = usePagination();

  // user selected from the sub user list
  const selectedUser = useSelector((s) => s.reports.selectedUser);

  // Current Selcted Item
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isDownoadingReport, setIsDownoadingReport] = useState(false);
  const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);

  const { localize } = useLocale(["reports"]);

  // Handling Filters
  const filterSchema = {
    status: "",
    requesterId: "",
    requestDate: "",
    requestBy: "",
    bankReferenceNumber: "",
    internalReferenceNumber: "",
    isReport: true,
  };
  const [filters, setFilters] = useState(filterSchema);
  const handleFilter = (filteredValues) => setFilters(filteredValues);
  const debouncedHandleFilters = useDebounce(handleFilter);

  const reportQuery = useQuery(
    ["report/prefund", filters, inputDate, page, selectedUser?.userHashId],
    () =>
      fetchReportPrefund({
        userHashId: selectedUser?.userHashId,
        userType: selectedUser?.productType || currentUser.productType,
        dateRange: inputDate.date,
        page,
        filters,
      }),
    {
      enabled: !!selectedUser,
    }
  );

  // download report
  const handleDownloadReport = () => {
    setIsDownoadingReport(true);
    downloadReporPrefund({
      clientHashId: selectedUser?.userHashId,
      dateRange: inputDate.date,
      email: currentUser.email,
      userType: currentUser.productType,
    })
      .then((res) => {
        setIsDownoadingReport(false);
      })
      .catch((err) => {});
  };

  return (
    <>
      <PageWrapper>
        {/* Page Header */}
        <Section>
          <div className="d-flex">
            <h3>{localize("heading")}</h3>
            <div className="ms-auto">
              <SubUserSelector
                clientHashId={currentUser.productType === APP_USERS.CLIENT}
              />
            </div>
          </div>
        </Section>

        {/* Table Data */}
        <Section>
          <>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <h5 className="text-muted">{localize("prefund.heading")}</h5>

              <Button
                text={localize("prefund.actions.downloadReport")}
                loading={isDownoadingReport}
                className="btn btn-primary"
                onClick={handleDownloadReport}
              />
            </div>

            {/* Table Data */}
            <Table
              loading={reportQuery.isLoading}
              cols={localize("prefund.table.cols", { returnObjects: true })}
              dataSize={reportQuery?.data?.prefundList?.length}
              filters={
                <TableFilters
                  filterSchema={filters}
                  handleFilter={debouncedHandleFilters}
                />
              }
            >
              {/* =============== Display Data from API =============*/}
              {reportQuery?.data?.prefundList?.map((item) => (
                <>
                  <Row>
                    {/* Status */}
                    <Data>
                      <StatusBadge status={item.status} />
                    </Data>

                    <Data>{item.requesterId ? item.requesterId : "-"}</Data>
                    <Data>
                      {item.dateOfCreation ? item.dateOfCreation : "-"}
                    </Data>
                    <Data>{item.requesterName ? item.requesterName : "-"}</Data>
                    <Data>
                      {item.bankReferenceNumber
                        ? item.bankReferenceNumber
                        : "-"}
                    </Data>
                    <Data>
                      {item.internalReferenceNumber
                        ? item.internalReferenceNumber
                        : "-"}
                    </Data>
                    <Data>
                      {getAmountWithCurrency(formatAmount(item.amount), "AED")}
                    </Data>

                    <Data>
                      {item.imageUrl ? (
                        <a
                          className="btn btn-light"
                          onClick={() => {
                            setCurrentRecord(item);
                            setIsDocumentDrawerOpen(true);
                          }}
                        >
                          <FileText size={12} />
                          <span className="ms-1">View Documents</span>
                        </a>
                      ) : (
                        <a className="btn btn-light disabled" disabled>
                          <FileText size={12} />
                          <span className="ms-1">View Documents</span>
                        </a>
                      )}
                    </Data>
                  </Row>
                </>
              ))}
              {/* show empty list */}
            </Table>

            {/* ======= Pagination =======*/}
            <div className="d-flex justify-content-end mb-5">
              <Pagination
                show={reportQuery?.data?.prefundList?.length}
                totalPages={reportQuery?.data?.pagination?.totalPages}
                handlePageSelect={(page) =>
                  setFilters({ ...filters, page: page })
                }
              />
            </div>
          </>
        </Section>

        {/* View Documents Drawer */}
        {isDocumentDrawerOpen && (
          <ViewDocumentDrawer
            title={localize("prefund.actions.viewDocuments")}
            cardItem={currentRecord}
            onClose={() => {
              setIsDocumentDrawerOpen(false);
            }}
          />
        )}
      </PageWrapper>
    </>
  );
};

const TableFilters = ({ filterSchema, handleFilter }) => {
  {
    /**
     *
     * Note : "name" attribute is important for filter controls
     * Interpreted as : [name] and filterSchema.name
     * keys in filter schema and [name] should be same
     *
     */
  }
  const statusOptions = [
    {
      value: "All",
      text: "All",
    },
    {
      value: "Approved",
      text: "Approved",
    },
    {
      value: "Pending",
      text: "Pending",
    },
    {
      value: "Declined",
      text: "Declined",
    },
  ];

  // table filter has its own state for persisting the filter values
  const [filteredValues, setFilteredValues] = useState(filterSchema);

  const handleFilteredValues = (event) => {
    const { name, value } = event.target;
    setFilteredValues({ ...filteredValues, [name]: value });
    handleFilter({ ...filteredValues, [name]: value, page: 0 });
  };

  return (
    <>
      <tr>
        <td>
          <Select
            name="status"
            value={filteredValues.status}
            onChange={(event) => handleFilteredValues(event)}
          >
            {statusOptions.map((option) => (
              <option value={option.value}>{option.text}</option>
            ))}
          </Select>
        </td>
        <td>
          <Input
            name="requesterId"
            value={filteredValues.requesterId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td></td>
        <td>
          <Input
            name="requestBy"
            value={filteredValues.requestBy}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="bankReferenceNumber"
            value={filteredValues.bankReferenceNumber}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="internalReferenceNumber"
            value={filteredValues.internalReferenceNumber}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td></td>
        <td></td>
      </tr>
    </>
  );
};

export default ReportsCardLoad;
